import React from 'react';
import { useTranslation } from 'react-i18next';
import FormGroupWithTooltip from '@/components/common/FormGroup/FormGroupWithTooltip';
import DatePicker from '@/components/common/DatePicker/DatePicker';
import { SimpleCheckbox } from '@/components/common/SimpleCheckbox/SimpleCheckbox';
import { IAlbum } from '@/data/models/album';
import { parseDate } from '@/utils/helper';
import { FormGroupProps } from './types';
import ReadOnlyField from '../../../common/ReadOnlyField/ReadOnlyField';

type Props = FormGroupProps & {
  isOriginalReleaseDateInvalid: (album: IAlbum) => boolean | undefined;
  getCurrentReleaseDate: (album: IAlbum) => Date | undefined;
  onOriginalReleaseDateChange: (date: Date | [Date, Date] | null) => void;
  originalReleaseDateOpen: boolean;
  setOriginalReleaseDateOpen: (open: boolean) => void;
  useReleaseDateVisible: boolean;
  activateUseReleaseDate: boolean;
  setActivateUseReleaseDate: (checked: boolean) => void;
  useReleaseDate: (album: IAlbum) => void;
};

export default function OriginalReleaseDateFormGroup({
  album,
  readonly,
  disabled,
  isOriginalReleaseDateInvalid,
  getCurrentReleaseDate,
  onOriginalReleaseDateChange,
  originalReleaseDateOpen,
  setOriginalReleaseDateOpen,
  useReleaseDateVisible,
  activateUseReleaseDate,
  setActivateUseReleaseDate,
  useReleaseDate,
  findErrorMessage,
}: Props) {
  const { t } = useTranslation();

  return (
    <>
      {!readonly ? (
        <FormGroupWithTooltip
          tooltipKey="tooltip.product.originalReleaseDate"
          hasErrors={isOriginalReleaseDateInvalid(album)}
          className="form-group original-release-date-form-group"
        >
          <DatePicker
            id="originalReleaseDate"
            containerClassName="original-release-date-picker"
            labelClassName="col-sm-3 form-label"
            label={t('product.editProduct.originalReleaseDate')}
            className={
              isOriginalReleaseDateInvalid(album)
                ? 'form-control invalid-input'
                : 'form-control'
            }
            calendarClassName={
              isOriginalReleaseDateInvalid(album) ? 'invalid-input' : ''
            }
            value={parseDate(album?.originalReleaseDate)}
            maxDate={getCurrentReleaseDate(album)}
            disabled={disabled}
            onChange={onOriginalReleaseDateChange}
            onCalendarOpen={() => setOriginalReleaseDateOpen(true)}
            onCalendarClose={() => setOriginalReleaseDateOpen(false)}
          />
          {useReleaseDateVisible ? (
            <div className="original-release-date-checkbox">
              <SimpleCheckbox
                text={t('product.editProduct.useReleaseDate')}
                checked={activateUseReleaseDate}
                onChange={(e) => {
                  useReleaseDate(album);
                  setActivateUseReleaseDate(e.target.checked);
                }}
              />
            </div>
          ) : null}
          {isOriginalReleaseDateInvalid(album) && originalReleaseDateOpen ? (
            <div className="col-sm-2 popUp-date-container">
              {findErrorMessage('OriginalReleaseDate')}
            </div>
          ) : null}
        </FormGroupWithTooltip>
      ) : (
        <ReadOnlyField
          id={'originalReleaseDate'}
          labelTitle={t('product.editProduct.originalReleaseDate')}
          value={album?.originalReleaseDate}
        ></ReadOnlyField>
      )}
    </>
  );
}
